import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Mail, X } from 'lucide-react';
import { fetchEmailReminder } from '../services/boardChatService';
import './BoardChatEmailReminderMessageCard.css';

export const EMAIL_REMINDER_CARD_PREFIX = 'Email enviado';

export function parseEmailReminderCardContent(content) {
  const text = String(content || '');
  if (!text.startsWith(EMAIL_REMINDER_CARD_PREFIX)) return null;
  const lines = text.split('\n').map((line) => line.trim()).filter(Boolean);
  const get = (label) => {
    const line = lines.find((item) => item.toLowerCase().startsWith(`${label.toLowerCase()}:`));
    return line ? line.slice(label.length + 1).trim() : '';
  };
  const reminderId = Number(get('Lembrete'));
  return {
    toEmails: get('Para') || '—',
    reminderId: Number.isFinite(reminderId) && reminderId > 0 ? reminderId : null,
  };
}

function sanitizeEmailHtml(html) {
  if (typeof document === 'undefined') return String(html || '');
  const wrap = document.createElement('div');
  wrap.innerHTML = String(html || '');
  wrap.querySelectorAll('script,iframe,object,embed').forEach((el) => el.remove());
  wrap.querySelectorAll('*').forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const name = String(attr.name || '');
      const value = String(attr.value || '');
      if (/^on/i.test(name) || (name === 'href' && /^\s*javascript:/i.test(value))) {
        el.removeAttribute(name);
      }
    });
  });
  return wrap.innerHTML;
}

function formatWhen(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const hour = String(d.getHours()).padStart(2, '0');
  const minute = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month} ${hour}:${minute}`;
}

export function BoardChatEmailReminderMessageCard({ data, conversationId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open || !conversationId || !data?.reminderId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setDetail(null);
    fetchEmailReminder(conversationId, data.reminderId)
      .then((res) => {
        if (cancelled) return;
        setDetail(res?.data || res || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Falha ao carregar o e-mail.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, conversationId, data?.reminderId]);

  const toLabel = String(data?.toEmails || '—').trim() || '—';

  const modal =
    open &&
    createPortal(
      <div
        className="bch-er-view-overlay"
        role="presentation"
        onClick={(e) => {
          if (e.target === e.currentTarget) setOpen(false);
        }}
      >
        <div
          className="bch-er-view-modal"
          role="dialog"
          aria-modal="true"
          aria-label="E-mail enviado"
        >
          <div className="bch-er-view-head">
            <h3 className="bch-er-view-title">E-mail enviado</h3>
            <button
              type="button"
              className="bch-er-view-close"
              aria-label="Fechar"
              onClick={() => setOpen(false)}
            >
              <X size={14} strokeWidth={1.8} />
            </button>
          </div>
          <div className="bch-er-view-body">
            {loading ? <p className="bch-er-view-status">Carregando…</p> : null}
            {error ? (
              <p className="bch-er-view-error" role="alert">
                {error}
              </p>
            ) : null}
            {!loading && !error && detail ? (
              <>
                <dl className="bch-er-view-meta">
                  <div>
                    <dt>Para</dt>
                    <dd>{detail.toEmails || toLabel}</dd>
                  </div>
                  {detail.ccEmails ? (
                    <div>
                      <dt>Cc</dt>
                      <dd>{detail.ccEmails}</dd>
                    </div>
                  ) : null}
                  {detail.bccEmails ? (
                    <div>
                      <dt>Cco</dt>
                      <dd>{detail.bccEmails}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>Assunto</dt>
                    <dd>{detail.subject || '—'}</dd>
                  </div>
                  {detail.createdAt ? (
                    <div>
                      <dt>Enviado</dt>
                      <dd>{formatWhen(detail.createdAt)}</dd>
                    </div>
                  ) : null}
                </dl>
                <div
                  className="bch-er-view-html"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeEmailHtml(detail.bodyHtml),
                  }}
                />
              </>
            ) : null}
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <button
        type="button"
        className="bch-email-msg-card"
        onClick={() => {
          if (!data?.reminderId) return;
          setOpen(true);
        }}
        disabled={!data?.reminderId}
      >
        <span className="bch-email-msg-icon" aria-hidden="true">
          <Mail size={17} strokeWidth={1.8} />
        </span>
        <span className="bch-email-msg-body">
          <strong>Email</strong>
          <span>{toLabel}</span>
        </span>
      </button>
      {modal}
    </>
  );
}
